/**
 * ## Search
 * The search module provides a way to send search queries to the backend.
 *
 * It will return a list containing the items of the search result.
 * The content of the search result depends on how the index was configured in the backend,
 * in addition the fields "_id" and "_score" will always be included.
 *
 * | Field  | Description |
 * | ---    | --- |
 * | _id    | The id of the matching item. |
 * | _score | The score of the search result. |
 *
 * ### Options
 * | Option | Type    | Description |
 * | ---    | ---     | --- |
 * | limit  | Integer | Max number of results returned. Default is 10, max is 100. |
 * | locale | String  | Searches in the specified locale. |
 * | filter | String  | Uses a predefined filter. |
 *
 * ### Examples
 *
 * #### Interface
 *     import { Search, SocketEnablerClient } from '@pbs/ngen-assets';
 *     const socketEnablerClient = new SocketEnablerClient();
 *     const search = new Search();
 *     search.init({ socketEnablerClient });
 *
 * #### Querying
 *  You can query a single collection.
 *
 *  search.search('event', {'locale': 'en'}, 'Hello', (err, result) => {
 *         // Handle the result
 *     });
 *
 *  Multiple collections. This is only supported when using Elasticsearch as the search engine.
 *
 *  search.search('event,market', {'locale': 'en'}, 'Hello', (err, result) => {
 *         // Handle the result
 *     });
 *
 * All collections. This is only supported when using Elasticsearch as the search engine.
 *
 *  search.search('*', {'locale': 'en'}, 'Hello', (err, result) => {
 *         // Handle the result
 *     });
 *
 * ##### Specify limit
 *  Set max number of returned results to 50
 *
 *  search.search('event', {'locale': 'en', 'limit': 50}, 'Hello', (err, result) => {
 *         // Handle the result
 *     });
 *
 * ##### Use a predefined filter
 * Assuming a filter with the name 'soccer' has been specified on the backend.
 * Note that filters only work when querying a single collection.
 *
 *  search.search('event', {'locale': 'en', filter: 'soccer'}, 'Malmö', (err, result) => {
 *         // Handle the result
 *     });
 *
 *
 * #### Get defined filters
 *     search.getFilters((err, result) => {
 *         // Handle the result
 *     });
 *
 * @module module:modules/search
 *
 * @public
 */

/**
 * Execute a search query.
 *
 * @param {String} collection The collection name, for example 'event'
 * @param {Object} options Map containing options. Valid options are 'locale', 'limit' and 'filter'
 * @param {String} query Search query
 * @param {Function} callback Callback handler for the results ``function (error, result) {}``,
 *                   where result is an array of search results.
 *
 * @public
 */

export default function Search() {
    let _socketEnablerClient;

    function init({ socketEnablerClient }) {
        _socketEnablerClient = socketEnablerClient;
        return this;
    }

    function search(collection, options, query, callback) {
        _socketEnablerClient.put(
            { name: 'search' },
            {
                query: query,
                limit: options['limit'],
                storable: collection,
                locale: options['locale'],
                page: options['pageNumber'],
                filter: options['filter'],
            },
            callback
        );
    }

    /**
     * Get all defined filters.
     *
     * @param {Function} callback Callback handler for the results ``function (error, result) {}``,
     *                   where result is an array of filters (storable, filterName).
     *
     * @public
     */
    function getFilters(callback) {
        _socketEnablerClient.put({ name: 'search/filters' }, {}, callback);
    }

    this.init = init;
    this.search = search;
    this.getFilters = getFilters;
}
